/**
 * Baby milestone cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_home_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const babyMilestoneCardsPack: ToilePack = {
    slug: 'baby-milestone-cards',
    category: 'classroom',
    name: 'Baby milestone cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'monthly', label: 'Monthly card', subject: 'a single card reading "Today I am" above a large "3 Months" with a small wreath of leaves and tiny stars around the words'},
        {key: 'firsts', label: 'Firsts card', subject: 'a single card reading "My First Tooth" with a small tooth drawing and an empty "Date:" line at the foot'},
        {key: 'about', label: 'All about me', subject: 'a single card titled "6 Months Old" with empty ruled lines after "I weigh", "I am this tall", "I love" and "I can"'},
    ],
    styles: [
        {key: 'watercolor', label: 'Soft watercolor', style: 'soft hand-painted watercolor wash with a delicate wreath of leaves and tiny stars around a clear writing area', palette: 'pale sage, blush and cream', framing: 'centered on a cream page with an even margin'},
        {key: 'boho', label: 'Boho neutral', style: 'minimal boho card with thin line arches, dried-grass sprigs and plenty of quiet space', palette: 'warm beige, terracotta and ivory', framing: 'centered on an ivory page with an even margin'},
        {key: 'mono', label: 'Black and white', style: 'clean black outline printable with uniform bold lines, plain boxes and simple leaf and star sprigs, nothing filled', palette: 'black ink only', framing: 'centered on a white page with an even margin'},
    ],
    directions: ['blue boy', 'pink girl', 'twins'],
    directionHint: 'try: blue boy',
    seo: {
        title: 'Baby Milestone Cards: Monthly, First Moment, About Me',
        metaDescription: 'Nine printable baby milestone cards: a monthly age card, a first-moment card and an all-about-me card, in three styles.',
        h1: 'Baby milestone card: monthly age, first moment, all about me',
        tagline: 'Nine printable cards: three each in soft watercolor, boho neutral and black and white.',
        intro: 'Printable milestone cards to set beside a baby for a photo each month and at every first. Parents fill in a few lines as the baby grows. The three cards are a monthly card reading Today I am over a large age, a first-moment card for a first tooth, and a six-month card with lines for weight, height, what the baby loves and what the baby can do. Each comes in three styles, quiet enough for any nursery.',
        useCases: [
            'a photo prop each month',
            'a first tooth or first step card',
            'a six-month all-about-me photo',
        ],
        faq: [
            {q: 'Which card is for a monthly photo?', a: 'The monthly age card, which reads Today I am over a large age in months.'},
            {q: 'What is the first-moment card?', a: 'A card with a small tooth drawing and a date line, for a first tooth or another first.'},
            {q: 'What goes on the all-about-me card?', a: 'Lines for weight, height, what the baby loves and what the baby can do, under the age.'},
            {q: 'Are the cards for a boy or a girl?', a: 'The samples are gender-neutral. Ask for a blue or pink look in the direction field to change that.'},
        ],
    },
}
